// src/lib/capability-inferencer.ts
// Infers capabilities from evidence using the taxonomy system

import {
  EvidenceItem,
  EnrichedEvidenceItem,
  InferredCapability,
  EvidenceSourceType
} from '../types/source-grounded';

import {
  findTechnology,
  searchTechnologies,
  findCertification,
  searchCertifications,
  findClearance,
  findAgency,
  searchAgencies,
  findNaics,
  findValueScaleRule,
  findDurationScaleRule,
  findTeamSizeRule,
  findCanonicalCapability,
  TechnologyNode,
  CertificationNode,
  ClearanceNode,
  AgencyDomainNode,
  NaicsRelationship,
  InferenceType,
  CapabilityProof
} from './taxonomy';

/**
 * Configuration for the capability inferencer
 */
export interface InferencerConfig {
  minConfidenceThreshold: number;    // Minimum confidence to include (default 0.5)
  maxInferencesPerEvidence: number;  // Limit inferences per evidence (default 20)
  enableTechnologyInference: boolean;
  enableCertificationInference: boolean;
  enableAgencyInference: boolean;
  enableScaleInference: boolean;
  enableNaicsInference: boolean;
}

const DEFAULT_CONFIG: InferencerConfig = {
  minConfidenceThreshold: 0.5,
  maxInferencesPerEvidence: 20,
  enableTechnologyInference: true,
  enableCertificationInference: true,
  enableAgencyInference: true,
  enableScaleInference: true,
  enableNaicsInference: true
};

/**
 * Capability Inferencer
 * Analyzes evidence items and infers capabilities based on taxonomy
 */
export class CapabilityInferencer {
  private config: InferencerConfig;

  constructor(config: Partial<InferencerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Enrich a single evidence item with inferred capabilities
   */
  enrichEvidence(evidence: EvidenceItem): EnrichedEvidenceItem {
    const inferences = this.inferFromEvidence(evidence);

    // Deduplicate and sort by confidence
    const uniqueInferences = this.deduplicateInferences(inferences);
    const sortedInferences = uniqueInferences
      .filter(inf => inf.confidence >= this.config.minConfidenceThreshold)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, this.config.maxInferencesPerEvidence);

    // Get all capability IDs (keywords + inferred)
    const allCapabilities = [
      ...(evidence.keywords || []),
      ...sortedInferences.map(inf => inf.capabilityId)
    ];

    return {
      ...evidence,
      inferredCapabilities: sortedInferences,
      allCapabilities: [...new Set(allCapabilities)]
    };
  }

  /**
   * Enrich multiple evidence items
   */
  enrichEvidenceArray(evidenceItems: EvidenceItem[]): EnrichedEvidenceItem[] {
    return evidenceItems.map(item => this.enrichEvidence(item));
  }

  /**
   * Infer capabilities from a single evidence item
   */
  private inferFromEvidence(evidence: EvidenceItem): InferredCapability[] {
    const inferences: InferredCapability[] = [];

    // Combine all searchable text
    const searchText = `${evidence.contentSummary} ${evidence.fullContent} ${evidence.keywords.join(' ')}`;

    // Technology inference
    if (this.config.enableTechnologyInference) {
      inferences.push(...this.inferFromTechnologies(evidence, searchText));
    }

    // Certification inference
    if (this.config.enableCertificationInference) {
      inferences.push(...this.inferFromCertifications(evidence, searchText));
    }

    // Agency domain inference
    if (this.config.enableAgencyInference) {
      inferences.push(...this.inferFromAgencies(evidence, searchText));
    }

    // Scale inference (contract value, duration, team size)
    if (this.config.enableScaleInference) {
      inferences.push(...this.inferFromScale(evidence));
    }

    // NAICS inference
    if (this.config.enableNaicsInference) {
      inferences.push(...this.inferFromNaics(evidence));
    }

    return inferences;
  }

  /**
   * Infer capabilities from technologies mentioned in evidence
   */
  private inferFromTechnologies(evidence: EvidenceItem, searchText: string): InferredCapability[] {
    const inferences: InferredCapability[] = [];
    const lowerText = searchText.toLowerCase();

    // Extract potential technology terms using common patterns
    const techTerms = this.extractTechTerms(lowerText);

    for (const term of techTerms) {
      const tech = findTechnology(term);
      if (tech) {
        inferences.push(...this.createInferencesFromProofs(
          evidence.evidenceId,
          tech.proves,
          'technology',
          tech.name,
          `${tech.name} → {capability}`
        ));
      }
    }

    return inferences;
  }

  /**
   * Extract technology terms from text
   */
  private extractTechTerms(text: string): string[] {
    const terms: string[] = [];

    // Common technology patterns to look for
    const techPatterns = [
      // Frontend frameworks
      /\b(react|reactjs|react\.js|angular|angularjs|vue|vue\.js|vuejs)\b/gi,
      // Backend
      /\b(node\.?js|nodejs|python|java|\.net|dotnet|c#|golang|go\s+language)\b/gi,
      // Databases
      /\b(postgresql|postgres|mysql|sql\s*server|oracle|mongodb|elasticsearch)\b/gi,
      // Cloud
      /\b(aws|amazon\s+web\s+services|azure|gcp|google\s+cloud|govcloud)\b/gi,
      // DevOps
      /\b(docker|kubernetes|k8s|terraform|ansible|jenkins|gitlab)\b/gi,
      // Security tools
      /\b(splunk|nessus|palo\s*alto)\b/gi,
      // Data tools
      /\b(tableau|power\s*bi|spark|hadoop)\b/gi,
      // AI/ML
      /\b(tensorflow|pytorch|machine\s+learning|ml|ai)\b/gi,
      // Collaboration
      /\b(jira|servicenow|sharepoint|teams)\b/gi,
      // Networking
      /\b(cisco|vmware|linux|windows\s+server)\b/gi
    ];

    for (const pattern of techPatterns) {
      const matches = text.match(pattern);
      if (matches) {
        terms.push(...matches.map(m => m.toLowerCase().trim()));
      }
    }

    return [...new Set(terms)];
  }

  /**
   * Infer capabilities from certifications mentioned in evidence
   */
  private inferFromCertifications(evidence: EvidenceItem, searchText: string): InferredCapability[] {
    const inferences: InferredCapability[] = [];
    const lowerText = searchText.toLowerCase();

    // Common certification patterns
    const certPatterns = [
      /\b(pmp|project\s+management\s+professional)\b/gi,
      /\b(cissp)\b/gi,
      /\b(cism)\b/gi,
      /\b(cisa)\b/gi,
      /\b(security\+|sec\+)\b/gi,
      /\b(ccna|ccnp)\b/gi,
      /\b(aws\s+(certified|solutions?\s+architect|developer|sysops|security))/gi,
      /\b(az-\d+|azure\s+(administrator|architect|security))/gi,
      /\b(csm|scrum\s*master)\b/gi,
      /\b(safe|scaled\s+agile)\b/gi,
      /\b(itil)\b/gi,
      /\b(fedramp)\b/gi,
      /\b(cmmc)\b/gi,
      /\b(iso\s*27001)\b/gi,
      /\b(six\s*sigma|green\s*belt|black\s*belt)\b/gi
    ];

    // Clearance patterns
    const clearancePatterns = [
      /\b(ts\/sci|top\s+secret\/sci)\b/gi,
      /\b(top\s+secret|ts\s+clearance)\b/gi,
      /\b(secret\s+clearance|secret\s+cleared)\b/gi,
      /\b(public\s+trust)\b/gi,
      /\b(q\s+clearance|l\s+clearance)\b/gi
    ];

    for (const pattern of certPatterns) {
      const matches = lowerText.match(pattern);
      if (matches) {
        for (const match of matches) {
          const cert = findCertification(match);
          if (cert) {
            inferences.push(...this.createInferencesFromProofs(
              evidence.evidenceId,
              cert.proves,
              'certification',
              cert.name,
              `${cert.name} certification → {capability}`
            ));
          }
        }
      }
    }

    for (const pattern of clearancePatterns) {
      const matches = lowerText.match(pattern);
      if (matches) {
        for (const match of matches) {
          const clearance = findClearance(match);
          if (clearance) {
            inferences.push(...this.createInferencesFromProofs(
              evidence.evidenceId,
              clearance.proves,
              'clearance',
              clearance.name,
              `${clearance.name} → {capability}`
            ));
          }
        }
      }
    }

    return inferences;
  }

  /**
   * Infer capabilities from agencies mentioned in evidence
   */
  private inferFromAgencies(evidence: EvidenceItem, searchText: string): InferredCapability[] {
    const inferences: InferredCapability[] = [];

    // Check metadata first
    if (evidence.metadata.agencyName) {
      const agency = findAgency(evidence.metadata.agencyName);
      if (agency) {
        inferences.push(...this.createInferencesFromProofs(
          evidence.evidenceId,
          agency.proves,
          'agency_domain',
          agency.name,
          `Experience with ${agency.name} → {capability}`
        ));
      }
    }

    // Search text for agency mentions
    const agencyPatterns = [
      /\b(department\s+of\s+(defense|veterans\s+affairs|health|homeland|energy|transportation|state|treasury|justice))\b/gi,
      /\b(dod|va|hhs|dhs|doe|dot|doj|dos)\b/gi,
      /\b(army|navy|air\s+force|space\s+force|marine\s+corps)\b/gi,
      /\b(disa|fema|cisa|fbi|cia|nsa|nasa)\b/gi,
      /\b(gsa|cms|cdc|nih|irs|faa|cbp)\b/gi,
      /\b(epa|sba)\b/gi
    ];

    const lowerText = searchText.toLowerCase();
    for (const pattern of agencyPatterns) {
      const matches = lowerText.match(pattern);
      if (matches) {
        for (const match of matches) {
          const agency = findAgency(match);
          if (agency) {
            inferences.push(...this.createInferencesFromProofs(
              evidence.evidenceId,
              agency.proves,
              'agency_domain',
              agency.name,
              `Experience with ${agency.name} → {capability}`
            ));
          }
        }
      }
    }

    return inferences;
  }

  /**
   * Infer capabilities from scale (contract value, duration, team size)
   */
  private inferFromScale(evidence: EvidenceItem): InferredCapability[] {
    const inferences: InferredCapability[] = [];

    // Contract value inference
    if (evidence.metadata.contractValue && evidence.metadata.contractValue > 0) {
      const valueRule = findValueScaleRule(evidence.metadata.contractValue);
      if (valueRule) {
        inferences.push(...this.createInferencesFromProofs(
          evidence.evidenceId,
          valueRule.proves,
          'scale',
          valueRule.label,
          `${valueRule.label} contract ($${(evidence.metadata.contractValue / 1000000).toFixed(1)}M) → {capability}`
        ));
      }
    }

    // Duration inference
    if (evidence.metadata.duration && evidence.metadata.duration > 0) {
      const durationRule = findDurationScaleRule(evidence.metadata.duration);
      if (durationRule) {
        inferences.push(...this.createInferencesFromProofs(
          evidence.evidenceId,
          durationRule.proves,
          'duration',
          durationRule.label,
          `${durationRule.label} (${evidence.metadata.duration} months) → {capability}`
        ));
      }
    }

    // Team size inference
    if (evidence.metadata.teamSize && evidence.metadata.teamSize > 0) {
      const teamRule = findTeamSizeRule(evidence.metadata.teamSize);
      if (teamRule) {
        inferences.push(...this.createInferencesFromProofs(
          evidence.evidenceId,
          teamRule.proves,
          'scale',
          teamRule.label,
          `${teamRule.label} (${evidence.metadata.teamSize} people) → {capability}`
        ));
      }
    }

    return inferences;
  }

  /**
   * Infer capabilities from NAICS code
   */
  private inferFromNaics(evidence: EvidenceItem): InferredCapability[] {
    const inferences: InferredCapability[] = [];

    if (evidence.metadata.naicsCode) {
      const naics = findNaics(evidence.metadata.naicsCode);
      if (naics) {
        inferences.push(...this.createInferencesFromProofs(
          evidence.evidenceId,
          naics.proves,
          'naics',
          `NAICS ${naics.code}`,
          `NAICS ${naics.code} (${naics.title}) → {capability}`
        ));
      }
    }

    return inferences;
  }

  /**
   * Create inference objects from capability proofs
   */
  private createInferencesFromProofs(
    evidenceId: string,
    proofs: CapabilityProof[],
    inferenceType: InferenceType,
    sourceName: string,
    chainTemplate: string
  ): InferredCapability[] {
    return proofs.map(proof => {
      const capabilityName = this.formatCapabilityName(proof.capability);
      return {
        capabilityId: proof.capability,
        capabilityName,
        confidence: proof.confidence,
        inferenceType,
        sourceEvidenceId: evidenceId,
        inferenceChain: chainTemplate.replace('{capability}', capabilityName),
        citationText: `${capabilityName} [inferred from ${sourceName}, ${evidenceId}]`
      };
    });
  }

  /**
   * Format capability ID as human-readable name
   */
  private formatCapabilityName(capabilityId: string): string {
    // Check if we have a canonical name
    const canonical = findCanonicalCapability(capabilityId);
    if (canonical) {
      return canonical.canonicalName;
    }

    // Convert snake_case to Title Case
    return capabilityId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Deduplicate inferences, keeping highest confidence for each capability
   */
  private deduplicateInferences(inferences: InferredCapability[]): InferredCapability[] {
    const byCapability = new Map<string, InferredCapability>();

    for (const inference of inferences) {
      const existing = byCapability.get(inference.capabilityId);
      if (!existing || inference.confidence > existing.confidence) {
        byCapability.set(inference.capabilityId, inference);
      }
    }

    return Array.from(byCapability.values());
  }
}

// Export singleton instance with default config
export const capabilityInferencer = new CapabilityInferencer();

// Export function for simple usage
export function inferCapabilities(evidence: EvidenceItem): EnrichedEvidenceItem {
  return capabilityInferencer.enrichEvidence(evidence);
}

export function inferCapabilitiesFromArray(evidenceItems: EvidenceItem[]): EnrichedEvidenceItem[] {
  return capabilityInferencer.enrichEvidenceArray(evidenceItems);
}
